/**
 * How each helpdesk is spelled for a human.
 *
 * Machine names providers with the slug the tool server keys on — `freshdesk`, `helpscout`,
 * `zoho` — and those slugs were reaching the screen verbatim, so the dialog said things like
 * "No channel list yet for freshdesk". Every other product surface writes these names
 * properly, and an operator reading a lowercase slug reasonably wonders whether they are
 * looking at a bug.
 *
 * Deliberately a lookup with a fallback rather than a capitalise() call. Half of these are
 * not one word with one capital — "Help Scout", "Zoho Desk", "Judge.me" — so a generic
 * transform would get them wrong in a way nobody notices until a customer does.
 */
const HELPDESK_LABELS: Record<string, string> = {
  gorgias: 'Gorgias',
  gorgias_ai: 'Gorgias',
  richpanel: 'Richpanel',
  freshdesk: 'Freshdesk',
  zendesk: 'Zendesk',
  kustomer: 'Kustomer',
  gladly: 'Gladly',
  helpscout: 'Help Scout',
  zoho: 'Zoho Desk'
};

/**
 * The display name for a provider slug.
 *
 * An unknown slug is title-cased rather than hidden or shown raw: a provider added to
 * machine before it is added here should still read as a name, and "Newdesk" is a better
 * wrong answer than "newdesk" or an empty space where a helpdesk should be.
 */
export const helpdeskLabel = (provider: string): string =>
  HELPDESK_LABELS[provider.toLowerCase()] ??
  provider.charAt(0).toUpperCase() + provider.slice(1);

/** The same, for a list — "Gorgias, Freshdesk". */
export const helpdeskLabels = (providers: readonly string[]): string =>
  providers.map(helpdeskLabel).join(', ');

/**
 * The helpdesks whose tickets carry a destination kim-ai-backend can match.
 *
 * All eight, on two tiers, and an operator never needs to know which tier a provider is on
 * — the picker shows a name and an id either way.
 *
 * ENDPOINT TIER, Gorgias and Richpanel: the row is one address, widget or line.
 *
 * CONTAINER TIER, the other six: the row is the provider's own grouping object — a Zendesk
 * or Kustomer brand, a Zoho department, a Freshdesk product, a Gladly inbox, a Help Scout
 * mailbox. One row per container, and the addresses inside it are not offered separately,
 * because a container cannot be half mapped the way a set of addresses can.
 *
 * What every entry shares: one provider-side id that covers every channel and appears on
 * every inbound ticket, so what an operator maps here and what ingest matches agree by
 * construction. `resolveConversationDestination` has a caller per provider in
 * kim-ai-backend, and this mirrors them.
 *
 * ADD A PROVIDER HERE when its ingest path starts resolving a destination, and not before:
 * offering a mapping that cannot work is worse than offering none, because it looks done.
 */
export const PROVIDERS_WITH_DESTINATION_RESOLUTION = new Set([
  'gorgias',
  'gorgias_ai',
  'richpanel',
  'zendesk',
  'zoho',
  'freshdesk',
  'gladly',
  'kustomer',
  'helpscout'
]);

/** Whether a destination mapped for this provider will ever match a ticket. */
export const resolvesDestinations = (provider: string): boolean =>
  PROVIDERS_WITH_DESTINATION_RESOLUTION.has(provider.trim().toLowerCase());

/**
 * The inbox a helpdesk connect creates, recognised by name.
 *
 * Machine names it `<provider>-inbox` (`HELPDESK_INBOX_NAMES` in inboxes.service.js) and
 * puts NO destination on it: the account-scoped helpdesks carry nothing on a ticket to
 * match, so their tickets take the brand off this inbox directly. That is the design, not
 * an unfinished setup, and counting it as "assigned but unmapped" showed every account
 * with a helpdesk connected a warning that could never be cleared.
 *
 * Matched on the name because the API gives nothing else to match on. `inboxes` is
 * candy's table and carries no provenance column, so the name machine wrote is the only
 * signal that this row was provisioned rather than created by an operator.
 */
const PROVIDER_INBOX_NAMES = new Set(
  Object.keys(HELPDESK_LABELS).map((provider) => `${provider}-inbox`)
);

/** Whether this inbox is the one a helpdesk connect provisioned. */
export const isProviderInbox = (name: string): boolean =>
  PROVIDER_INBOX_NAMES.has(name.trim().toLowerCase());
